/**
 * Экран «Редактор планировки».
 * Отвечает только за раскладку виджетов и выбор рабочей области по режиму просмотра.
 *
 * Панель свойств — отдельная колонка рядом с рабочей областью: она не перекрывает
 * вкладки помещений и не мешает их прокрутке. Панель инструментов в 2D остаётся
 * наложенной на холст, чтобы открытие выдвижной секции не сдвигало чертёж.
 *
 * 3D-виджет подгружается лениво: three.js весит больше всего остального
 * приложения, а нужен только в режиме просмотра «3D». Смета — тоже лениво: это лист
 * во всю рабочую область, без панели инструментов и свойств.
 */
import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLoaderData, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { apiHooks } from "@/api";
import { AnalyticsGoal, trackGoal } from "@/shared/lib/analytics";
import { LocalStorageUtils } from "@/shared/lib/storage";
import { authUserId, useAuthStore, useProjectStore } from "@/shared/store";
import { UIKit } from "@/shared/ui";
import { useEditorStore } from "@/widgets/editor";
import { ViewMode } from "@/shared/store";
import { PlanEditor2D } from "@/widgets/plan-editor-2d";
import { EditorRightPropertiesPanel } from "@/widgets/properties-panel";
import { StatusBar } from "@/widgets/status-bar";
import { EditorLeftToolPanel } from "@/widgets/tool-panel";
import { WallElevations } from "@/widgets/wall-elevations";
import { Toolbar } from "@/widgets/toolbar";
import { ProjectSyncState, useProjectSync } from "@/widgets/plan-storage";
import { DemoModeBanner, ProjectPickerModal, ReadOnlyBanner } from "./ui";

const Scene3D = lazy(() => import("@/widgets/scene-3d").then((m) => ({ default: m.Scene3D })));
const Estimate = lazy(() => import("@/widgets/estimate").then((m) => ({ default: m.Estimate })));

const EDITOR_PATHS: Record<ViewMode, string> = {
	[ViewMode.TwoD]: "/editor/2d",
	[ViewMode.ThreeD]: "/editor/3d",
	[ViewMode.Elevation]: "/editor/elevations",
	[ViewMode.Estimate]: "/editor/estimate",
	[ViewMode.Split]: "/editor/2d",
};

const MOBILE_VIEW_ONLY_QUERY = "(max-width: 767px)";
const PICKER_SESSION = crypto.randomUUID();

export const EditorPage = () => {
	const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
	const userId = useAuthStore((state) => authUserId(state.accessToken));
	const projectStore = useProjectStore();
	const editorStore = useEditorStore();
	const { demo, loadEmptyDemo } = projectStore;
	const currentProjectId = projectStore.project.id;
	const layers = projectStore.project.layers;
	const { viewMode, pdfExportProgress, overlayTopOffset, setReadOnly, setViewMode } = editorStore;
	const currentViewMode = useRef(viewMode);
	const { pathname, search, state: locationState } = useLocation();
	const navigate = useNavigate();
	const { projectId, viewMode: routeViewMode } = useLoaderData() as {
		projectId: string | null;
		viewMode: ViewMode;
	};
	const { isReadOnly, state: syncState } = useProjectSync(projectId);
	const [isMobileView, setIsMobileView] = useState(
		() => typeof window !== "undefined" && window.matchMedia(MOBILE_VIEW_ONLY_QUERY).matches,
	);
	const effectiveReadOnly =
		isMobileView || (!demo && (isReadOnly || (projectId !== null && !isAuthenticated)));
	const [demoBannerHidden, setDemoBannerHidden] = useState(false);
	const [pickerOpen, setPickerOpen] = useState(false);
	const [pendingSelection, setPendingSelection] = useState<(() => Promise<void>) | null>(null);
	const shouldRestore =
		isAuthenticated && projectId === null && locationState?.pickerSession !== PICKER_SESSION;
	const lastProject =
		shouldRestore && userId !== null ? LocalStorageUtils.getLastProject(userId) : null;
	const lastProjectQuery = apiHooks.projects.useProjectData(userId, lastProject, shouldRestore);
	const restoring =
		shouldRestore &&
		lastProject !== null &&
		!lastProjectQuery.isError &&
		(lastProjectQuery.isChecking || lastProjectQuery.response?.data != null);
	const [scene3dMounted, setScene3dMounted] = useState(false);
	// Лоадер 3D держится, пока сцена не нарисована целиком, а не только пока качается её код
	const [scene3dReady, setScene3dReady] = useState(false);
	/** Сколько процентов моделей 3D скачано — полоса под чертежом лоадера */
	const [scene3dProgress, setScene3dProgress] = useState<number | null>(null);
	const [scene3dLoaderGone, setScene3dLoaderGone] = useState(false);
	if (viewMode === ViewMode.ThreeD && !scene3dMounted) setScene3dMounted(true);
	useLayoutEffect(() => {
		currentViewMode.current = viewMode;
	}, [viewMode]);
	useLayoutEffect(() => {
		currentViewMode.current = routeViewMode;
		setViewMode(routeViewMode);
	}, [routeViewMode, setViewMode]);
	useLayoutEffect(() => {
		if (projectId === null && !isAuthenticated && locationState?.pickerSession !== PICKER_SESSION) {
			loadEmptyDemo();
		}
	}, [isAuthenticated, locationState?.pickerSession, projectId, loadEmptyDemo]);
	useEffect(() => {
		if (!shouldRestore) return;
		if (lastProject !== null) {
			if (lastProjectQuery.isChecking || lastProjectQuery.isError) return;
			if (lastProjectQuery.response?.data != null) {
				const params = new URLSearchParams(search);
				params.set("projectId", lastProject);
				navigate({ pathname, search: params.toString() }, { replace: true });
			} else if (lastProjectQuery.response?.error?.code === "not_found" && userId !== null) {
				LocalStorageUtils.clearLastProject(userId);
			}
		}
	}, [
		lastProject,
		lastProjectQuery.isChecking,
		lastProjectQuery.isError,
		lastProjectQuery.response,
		navigate,
		pathname,
		search,
		shouldRestore,
		userId,
	]);
	useEffect(() => {
		if (
			userId !== null &&
			projectId !== null &&
			currentProjectId === projectId &&
			!demo &&
			syncState === ProjectSyncState.Ready
		) {
			LocalStorageUtils.setLastProject(userId, projectId);
		}
	}, [currentProjectId, demo, projectId, syncState, userId]);
	useEffect(() => {
		// После перехода по URL layout effect уже обновляет store. Берём его актуальное значение,
		// а не viewMode из замыкания первого рендера, иначе /editor/elevations успевает вернуть
		// пользователя на /editor/2d до следующего рендера.
		if (viewMode !== currentViewMode.current) return;
		const routeForViewMode = EDITOR_PATHS[currentViewMode.current];
		if (pathname !== routeForViewMode) {
			navigate({ pathname: routeForViewMode, search }, { state: locationState });
		}
	}, [locationState, navigate, pathname, search, viewMode]);
	useEffect(() => {
		const query = window.matchMedia(MOBILE_VIEW_ONLY_QUERY);
		const updateMobile = () => setIsMobileView(query.matches);
		updateMobile();
		query.addEventListener("change", updateMobile);
		return () => query.removeEventListener("change", updateMobile);
	}, []);
	useEffect(() => {
		setReadOnly(effectiveReadOnly);
		return () => setReadOnly(false);
	}, [effectiveReadOnly, setReadOnly]);

	// В простое браузера заранее подтягиваем код 3D и модели проекта: первый переход
	// в 3D тогда почти мгновенный. Модели догружаются и когда проект меняется — открыли
	// демо, загрузили файл, поставили новый предмет; уже скачанные повторно не качаются.
	// В режиме экономии трафика модели не качаем — только код
	useEffect(() => {
		const saveData =
			(navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData ===
			true;
		// Safari простоя не знает — там просто откладываем на полторы секунды
		const whenIdle = (callback: () => void) =>
			typeof window.requestIdleCallback === "function"
				? window.requestIdleCallback(callback, { timeout: 4000 })
				: window.setTimeout(callback, 1500);
		const cancelIdle = (id: number) =>
			typeof window.cancelIdleCallback === "function"
				? window.cancelIdleCallback(id)
				: window.clearTimeout(id);
		let idle: number | undefined;
		let stopped = false;
		const preload = () => {
			idle = undefined;
			import("@/widgets/scene-3d")
				.then((scene3d) => {
					if (!stopped && !saveData) {
						scene3d.preloadScene3DModels(layers);
					}
				})
				.catch((error: unknown) => {
					// Не страшно: 3D догрузится при открытии, как раньше
					console.warn("3D: предзагрузка не удалась", error);
				});
		};
		const schedule = () => {
			if (idle !== undefined) return;
			idle = whenIdle(preload);
		};
		schedule();
		return () => {
			stopped = true;
			if (idle !== undefined) cancelIdle(idle);
		};
	}, [layers]);
	// Проект грузится с сервера — тот же чертёж, что у прелоадера и лоадера 3D
	if (syncState === ProjectSyncState.Loading || restoring) {
		return (
			<div className="flex flex-1 flex-col bg-surface" role="status" aria-label="Открываем проект">
				<UIKit.BlueprintLoader />
			</div>
		);
	}
	if (syncState === ProjectSyncState.Error) {
		return (
			<div className="grid flex-1 place-items-center bg-surface text-ink">
				Не удалось открыть проект.
			</div>
		);
	}

	const selectProject = (action: () => Promise<void>) => {
		if (demo && projectStore.past.length > 0) {
			setPendingSelection(() => action);
		} else {
			void action();
		}
	};
	const saveBeforeSwitch = async () => {
		if (demo || projectId === null) return true;
		const save = editorStore.saveProject;
		if (save === null) return true;
		try {
			await save();
			return true;
		} catch {
			toast.error("Не удалось сохранить проект перед переключением.");
			return false;
		}
	};
	const openTemporary = async (sample: boolean) => {
		if (!(await saveBeforeSwitch())) return;
		setPickerOpen(false);
		if (sample) projectStore.loadSample();
		else {
			projectStore.loadEmptyDemo();
			trackGoal(AnalyticsGoal.EmptyProjectStarted);
		}
		const params = new URLSearchParams(search);
		params.delete("projectId");
		navigate(
			{ pathname, search: params.toString() },
			{ replace: true, state: { pickerSession: PICKER_SESSION } },
		);
	};

	return (
		<div className="flex min-h-0 flex-1 flex-col">
			{(pickerOpen || (projectId === null && locationState?.pickerSession !== PICKER_SESSION)) && (
				<ProjectPickerModal
					isAuthenticated={isAuthenticated}
					onClose={() => {
						setPickerOpen(false);
						navigate(
							{ pathname, search },
							{ replace: true, state: { pickerSession: PICKER_SESSION } },
						);
					}}
					onOpen={(id) => {
						selectProject(async () => {
							if (id !== projectId && !(await saveBeforeSwitch())) return;
							setPickerOpen(false);
							if (id === projectId && demo) projectStore.exitDemo();
							const params = new URLSearchParams(search);
							params.set("projectId", id);
							navigate({ pathname, search: params.toString() }, { replace: true });
						});
					}}
					onDemo={() => {
						selectProject(() => openTemporary(true));
					}}
					onEmpty={() => {
						selectProject(() => openTemporary(false));
					}}
				/>
			)}
			{pendingSelection !== null && (
				<UIKit.ConfirmModal
					title="Переключить проект?"
					description="Изменения в текущем временном проекте не сохранятся."
					onClose={() => setPendingSelection(null)}
					actions={
						<>
							<UIKit.Button onClick={() => setPendingSelection(null)}>Отмена</UIKit.Button>
							<UIKit.Button
								onClick={async () => {
									setPendingSelection(null);
									await pendingSelection();
								}}
							>
								Переключить
							</UIKit.Button>
						</>
					}
				/>
			)}
			<div className="relative min-h-0 flex-1">
				<Toolbar projectId={projectId} onSelectProject={() => setPickerOpen(true)} />
				{demo && !demoBannerHidden && (
					<DemoModeBanner
						isAuthenticated={isAuthenticated}
						onClose={() => setDemoBannerHidden(true)}
					/>
				)}
				{effectiveReadOnly && <ReadOnlyBanner />}
				<div className="absolute inset-0 min-w-0 bg-surface">
					<div className="absolute inset-0">
						{scene3dMounted && (
							<div
								className="absolute inset-0"
								style={viewMode === ViewMode.ThreeD ? undefined : { display: "none" }}
							>
								<Suspense fallback={null}>
									<Scene3D onReady={() => setScene3dReady(true)} onProgress={setScene3dProgress} />
								</Suspense>
								{/* Один лоадер на всё время загрузки: код, модели, шейдеры. Гаснет плавно,
								    когда сцена уже нарисована, — без пустого фона и скачка */}
								{!scene3dLoaderGone && (
									<div
										className={`absolute inset-0 z-30 bg-surface transition-opacity duration-300 ease-out${
											scene3dReady ? " pointer-events-none opacity-0" : ""
										}`}
										onTransitionEnd={() => setScene3dLoaderGone(true)}
									>
										<UIKit.BlueprintLoader
											progress={scene3dProgress}
											progressLabel="Загружаем модели"
										/>
									</div>
								)}
							</div>
						)}
						{viewMode === ViewMode.Elevation && <WallElevations />}
						{viewMode === ViewMode.Estimate && (
							<Suspense fallback={null}>
								<Estimate />
							</Suspense>
						)}
						{(viewMode === ViewMode.TwoD || viewMode === ViewMode.Split) && <PlanEditor2D />}
						{viewMode !== ViewMode.Estimate && <EditorRightPropertiesPanel />}
					</div>
					{viewMode !== ViewMode.Estimate && (viewMode !== ViewMode.ThreeD || scene3dMounted) ? (
						// Левая панель — над подписями и ручками 3D-сцены (Html до z-25): ручка поворота
						// дивана за панелью не должна рисоваться поверх каталога
						<div
							className="pointer-events-none absolute bottom-2 left-2 z-30 flex items-start overflow-visible"
							style={{ top: overlayTopOffset }}
						>
							<EditorLeftToolPanel
								projectId={projectId}
								onSelectProject={() => setPickerOpen(true)}
							/>
						</div>
					) : null}
				</div>
			</div>
			<StatusBar />
			{pdfExportProgress && (
				<div className="absolute inset-0 z-50 grid place-items-center bg-surface/85 text-ink backdrop-blur-sm">
					<div className="border border-border bg-surface px-6 py-5 text-center shadow-lg">
						<UIKit.Text as="p">Готовим PDF</UIKit.Text>
						<UIKit.Text as="p" size="small" className="mt-1">
							{pdfExportProgress}
						</UIKit.Text>
					</div>
				</div>
			)}
		</div>
	);
};
